import { useRef, useState } from "react";
import { toast } from "sonner";
import { Crown, GripVertical, Plus, Trash2, Pencil, Check, X, Users, Building2, UserX, GitBranchPlus, FolderInput, ZoomIn, ZoomOut, FileDown, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { api } from "../../lib/api";

export const DEPT_COLORS = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#db2777", "#475569"];
const err = (e) => toast.error(e?.response?.data?.detail || "تعذّر الحفظ");

const Avatar = ({ u, size = "h-8 w-8" }) => (u.picture
  ? <img src={u.picture} alt="" className={`${size} shrink-0 rounded-full object-cover`} />
  : <span className={`${size} grid shrink-0 place-items-center rounded-full bg-slate-200 text-xs font-bold text-slate-600`}>{(u.name || "?").trim()[0]}</span>);

export const UserChip = ({ u, ctx, onOpen, accent }) => {
  const role = ctx.roles.find((r) => r.id === u.role_id);
  const title = ctx.job_titles.find((j) => j.id === u.job_title_id);
  return (
    <div draggable={!u.is_primary_owner} onDragStart={(e) => { e.dataTransfer.setData("text/uid", u.uid); e.dataTransfer.effectAllowed = "move"; }}
      onClick={() => onOpen(u)} data-testid={`org-user-${u.uid}`}
      className="group flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 transition-[box-shadow,transform,border-color] duration-150 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      {!u.is_primary_owner && <GripVertical data-noexport className="mt-2 h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-slate-500" />}
      <Avatar u={u} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="break-words text-sm font-semibold leading-5 text-slate-800 [overflow-wrap:anywhere]" title={u.name}>{u.name}</p>
        <div className="flex flex-wrap items-center gap-1">
          <span className={`text-[11px] leading-4 ${title ? "text-slate-600" : "text-slate-400"}`}>{title?.name || "بدون مسمى"}</span>
          {role && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold leading-4 text-white" style={{ background: accent || role.color || "#0f172a" }}>{role.name}</span>}
          {u.status === "invited" && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] leading-4 text-amber-700">مدعو</span>}
        </div>
      </div>
    </div>
  );
};

const useDrop = (onDrop) => {
  const [over, setOver] = useState(false);
  return [over, {
    onDragOver: (e) => { e.preventDefault(); setOver(true); },
    onDragLeave: () => setOver(false),
    onDrop: (e) => { e.preventDefault(); setOver(false); const uid = e.dataTransfer.getData("text/uid"); if (uid) onDrop(uid); },
  }];
};

const descendants = (deps, id) => {
  const out = [id];
  for (let i = 0; i < out.length; i++) deps.forEach((x) => { if (x.parent_id === out[i] && !out.includes(x.id)) out.push(x.id); });
  return out;
};

const DeptHeader = ({ d, ctx, reload, onSub }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(d.name);
  const [parent, setParent] = useState(d.parent_id || "");
  const [colorOpen, setColorOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const blocked = descendants(ctx.departments, d.id);
  const save = async (body) => { try { await api.orgEditDept(d.id, body); setEdit(false); reload(); } catch (e) { err(e); } };
  const del = async () => { if (!window.confirm(`حذف قسم «${d.name}»؟`)) return; try { await api.orgDelDept(d.id); reload(); } catch (e) { err(e); } };
  if (edit) return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8" data-testid={`dept-name-input-${d.id}`} autoFocus />
        <Button size="icon" className="h-8 w-8 shrink-0" onClick={() => save({ name, parent_id: parent })} data-testid={`dept-name-save-${d.id}`}><Check className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setEdit(false)}><X className="h-4 w-4" /></Button>
      </div>
      <label className="flex items-center gap-1.5 text-[11px] text-slate-500">تابع لـ
        <select value={parent} onChange={(e) => setParent(e.target.value)} className="h-7 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-1 text-xs" data-testid={`dept-parent-select-${d.id}`}>
          <option value="">— قسم رئيسي —</option>
          {ctx.departments.filter((x) => !blocked.includes(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </label>
    </div>
  );
  return (
    <div className="flex flex-col gap-1">
      <div className="min-w-0">
        <h3 className="break-words text-base font-bold leading-6 text-slate-900" data-testid={`dept-title-${d.id}`}>{d.name}</h3>
        <p className="text-[11px] text-slate-500">{d.employees} موظف في السجلات</p>
        {!d.parent_id && (() => { const t = totalStaff(ctx, d); return t.subs > 0 && (
          <p className="mt-1 w-fit rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold leading-4 text-blue-700" data-testid={`dept-total-${d.id}`}>
            <span>الإجمالي مع {t.subs} {t.subs === 1 ? "فرع" : "فروع"}</span><br /><span>{t.emp} موظف · {t.users} مستخدم</span>
          </p>); })()}
      </div>
      <div data-noexport className="order-first -mb-1 -mt-1 flex items-center gap-0.5 self-end opacity-60 transition-opacity group-hover/dept:opacity-100">
        <Popover open={colorOpen} onOpenChange={setColorOpen}>
          <PopoverTrigger asChild>
            <button type="button" title="لون القسم" className="rounded p-1 hover:bg-slate-100" data-testid={`dept-color-${d.id}`}><span className="block h-3.5 w-3.5 rounded-full" style={{ background: d.color || DEPT_COLORS[0] }} /></button>
          </PopoverTrigger>
          <PopoverContent className="flex w-auto gap-1.5 p-2" data-testid={`dept-color-menu-${d.id}`}>
            {DEPT_COLORS.map((c) => <button key={c} type="button" onClick={() => { setColorOpen(false); save({ color: c }); }} className={`h-5 w-5 rounded-full ring-offset-2 hover:ring-2 ${d.color === c ? "ring-2" : ""}`} style={{ background: c, "--tw-ring-color": c }} data-testid={`dept-color-opt-${c.slice(1)}`} />)}
          </PopoverContent>
        </Popover>
        <button type="button" onClick={onSub} title="إضافة قسم فرعي" className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`dept-add-sub-${d.id}`}><GitBranchPlus className="h-3.5 w-3.5" /></button>
        <Popover open={moveOpen} onOpenChange={setMoveOpen}>
          <PopoverTrigger asChild>
            <button type="button" title="نقل تحت قسم آخر" className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`dept-move-${d.id}`}><FolderInput className="h-3.5 w-3.5" /></button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-1.5" data-testid={`dept-move-menu-${d.id}`}>
            <p className="px-2 pb-1 pt-0.5 text-[11px] font-semibold text-slate-500">نقل «{d.name}» تحت:</p>
            {[{ id: "", name: "الشركة مباشرة (قسم رئيسي)" }, ...ctx.departments.filter((x) => !blocked.includes(x.id))].map((x) => (
              <button key={x.id || "root"} type="button" disabled={(d.parent_id || "") === x.id}
                onClick={() => { setMoveOpen(false); save({ parent_id: x.id }); }}
                className="block w-full rounded-md px-2 py-1.5 text-start text-sm hover:bg-slate-100 disabled:opacity-40" data-testid={`dept-move-opt-${d.id}-${x.id || "root"}`}>{x.name}</button>
            ))}
          </PopoverContent>
        </Popover>
        <button type="button" onClick={() => { setName(d.name); setParent(d.parent_id || ""); setEdit(true); }} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`dept-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={del} className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`dept-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
};

const AddSub = ({ d, reload, close }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.orgAddDept({ name: name.trim(), color: d.color, parent_id: d.id }); toast.success("تمت إضافة القسم الفرعي"); close(); reload(); } catch (e) { err(e); }
  };
  return (
    <div className="mt-2 flex items-center gap-1.5 rounded-xl bg-slate-50 p-1.5" data-testid={`dept-sub-form-${d.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="اسم القسم الفرعي" className="h-8" autoFocus data-testid={`dept-sub-input-${d.id}`} />
      <Button size="icon" className="h-8 w-8 shrink-0" onClick={add} disabled={!name.trim()} data-testid={`dept-sub-save-${d.id}`}><Check className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={close}><X className="h-4 w-4" /></Button>
    </div>
  );
};

const subtreeIds = (ctx, id) => [id, ...ctx.departments.filter((x) => x.parent_id === id).flatMap((x) => subtreeIds(ctx, x.id))];
const totalStaff = (ctx, d) => {
  const ids = subtreeIds(ctx, d.id);
  return { emp: ctx.departments.filter((x) => ids.includes(x.id)).reduce((s, x) => s + (x.employees || 0), 0), users: ctx.users.filter((u) => ids.includes(u.department_id)).length, subs: ids.length - 1 };
};

const branch = (d, ctx, onOpen, reload, depth = 0, fold) => {
  const kids = ctx.departments.filter((x) => x.parent_id === d.id);
  const closed = fold.closed.includes(d.id);
  return (
    <div key={d.id} className="flex flex-col items-center" data-testid={`dept-branch-${d.id}`}>
      <DeptCard d={d} ctx={ctx} onOpen={onOpen} reload={reload} depth={depth} />
      {kids.length > 0 && <>
        <span className="h-3 w-px bg-slate-300" />
        <button type="button" onClick={() => fold.toggle(d.id)} data-noexport={closed ? undefined : ""}
          className="z-10 -my-1 flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50" data-testid={`dept-fold-${d.id}`}>
          {closed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          {closed ? `عرض ${kids.length} ${kids.length === 1 ? "فرع" : "فروع"}` : "طي الفروع"}
        </button>
        {!closed && <>
          <span className="h-3 w-px bg-slate-300" />
          <div className="flex flex-wrap justify-center gap-4 rounded-2xl border border-dashed border-slate-200 p-3" data-testid={`dept-children-${d.id}`}>
            {kids.map((k) => branch(k, ctx, onOpen, reload, depth + 1, fold))}
          </div>
        </>}
      </>}
    </div>
  );
};

const DeptCard = ({ d, ctx, onOpen, reload, depth }) => {
  const color = d.color || "#2563eb";
  const [sub, setSub] = useState(false);
  const manager = ctx.users.find((u) => u.uid === d.manager_user_id);
  const members = ctx.users.filter((u) => u.department_id === d.id && u.uid !== d.manager_user_id);
  const run = (p) => p.then(reload).catch(err);
  const [overM, dropM] = useDrop((uid) => run(api.orgEditDept(d.id, { manager_user_id: uid })));
  const [overB, dropB] = useDrop((uid) => run(api.orgPlaceUser(uid, { department_id: d.id })));
  return (
    <div data-testid={`dept-card-${d.id}`} className={`group/dept relative w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${depth ? "sm:w-[calc(var(--dw,300px)*0.9)]" : "sm:w-[var(--dw,300px)]"}`} style={{ borderTop: `4px solid ${color}` }}>
      {!depth && <span className="absolute -top-6 start-1/2 hidden h-5 w-px bg-slate-300 sm:block" />}
      <DeptHeader d={d} ctx={ctx} reload={reload} onSub={() => setSub(true)} />
      {sub && <AddSub d={d} reload={reload} close={() => setSub(false)} />}
      <div {...dropM} data-testid={`dept-manager-slot-${d.id}`} className={`mt-3 rounded-xl border-2 border-dashed p-1.5 transition-colors ${overM ? "border-amber-400 bg-amber-50" : manager ? "border-transparent bg-amber-50/50" : "border-slate-200"}`}>
        <p className="mb-1 flex items-center gap-1 px-1 text-[11px] font-bold text-amber-700"><Crown className="h-3 w-3" /> مدير القسم</p>
        {manager ? <UserChip u={manager} ctx={ctx} onOpen={onOpen} accent={color} />
          : <p data-noexport className="px-1 pb-1 text-xs text-slate-400">اسحب مستخدماً هنا ليصبح مدير القسم</p>}
      </div>
      <div {...dropB} data-testid={`dept-members-${d.id}`} className={`mt-2 min-h-[64px] space-y-1.5 rounded-xl p-1.5 transition-colors ${overB ? "bg-blue-50 ring-2 ring-blue-300" : ""}`}>
        <p className="flex items-center gap-1 px-1 text-[11px] font-bold text-slate-500"><Users className="h-3 w-3" /> الأعضاء ({members.length})</p>
        {members.map((u) => <UserChip key={u.uid} u={u} ctx={ctx} onOpen={onOpen} />)}
        {!members.length && <p data-noexport className="px-1 text-xs text-slate-400">اسحب المستخدمين إلى هنا</p>}
      </div>
    </div>
  );
};

const AddDept = ({ reload, used = [] }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.orgAddDept({ name: name.trim(), color: DEPT_COLORS.find((c) => !used.includes(c)) || DEPT_COLORS[used.length % DEPT_COLORS.length] }); setName(""); reload(); toast.success("تمت إضافة القسم"); } catch (e) { err(e); }
  };
  return (
    <div data-noexport className="flex w-full flex-col justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 p-4 sm:w-[var(--dw,300px)]">
      <p className="flex items-center gap-1.5 text-sm font-bold text-slate-600"><Plus className="h-4 w-4" /> قسم جديد</p>
      <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="مثال: الموارد البشرية" data-testid="org-new-dept-input" />
      <Button onClick={add} disabled={!name.trim()} data-testid="org-new-dept-btn">إضافة القسم</Button>
    </div>
  );
};

const SIZES = [["compact", "مضغوط", 230], ["normal", "عادي", 300], ["wide", "واسع", 380]];
const saved = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

const ChartToolbar = ({ zoom, setZoom, size, setSize, onExport, busy, fold }) => (
  <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white/90 p-2 shadow-sm backdrop-blur" data-testid="org-toolbar">
    <div className="flex items-center gap-1">
      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={zoom <= 0.4} onClick={() => setZoom(Math.max(0.4, +(zoom - 0.1).toFixed(1)))} data-testid="org-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
      <button type="button" onClick={() => setZoom(1)} className="w-14 rounded-md py-1 text-center text-xs font-bold tabular-nums text-slate-700 hover:bg-slate-100" title="إرجاع 100%" data-testid="org-zoom-value">{Math.round(zoom * 100)}%</button>
      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={zoom >= 1.6} onClick={() => setZoom(Math.min(1.6, +(zoom + 0.1).toFixed(1)))} data-testid="org-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
      <input type="range" min={40} max={160} step={10} value={Math.round(zoom * 100)} onChange={(e) => setZoom(+e.target.value / 100)} className="hidden w-28 accent-blue-600 sm:block" data-testid="org-zoom-range" />
    </div>
    <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-0.5" data-testid="org-size">
      {SIZES.map(([k, l]) => (
        <button key={k} type="button" onClick={() => setSize(k)} className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${size === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid={`org-size-${k}`}>{l}</button>
      ))}
    </div>
    {fold}
    <Button size="sm" onClick={onExport} disabled={busy} className="gap-1.5" data-testid="org-export-pdf">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} تصدير PDF</Button>
  </div>
);

const exportPdf = async (node, name) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, ignoreElements: (el) => el.hasAttribute?.("data-noexport") });
  const pdf = new jsPDF({ orientation: canvas.width >= canvas.height ? "landscape" : "portrait", unit: "pt", format: "a3" });
  const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight(), m = 24;
  const r = Math.min((pw - m * 2) / canvas.width, (ph - m * 2) / canvas.height);
  const w = canvas.width * r, h = canvas.height * r;
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", (pw - w) / 2, (ph - h) / 2, w, h);
  pdf.save(`${(name || "الهيكل التنظيمي").replace(/[\\/:*?"<>|]/g, "")} - الهيكل التنظيمي.pdf`);
};

export const OrgChart = ({ ctx, onOpen, reload }) => {
  const owners = ctx.users.filter((u) => u.role === "manager");
  const free = ctx.users.filter((u) => u.role !== "manager" && !ctx.departments.some((d) => d.id === u.department_id));
  const [overF, dropF] = useDrop((uid) => api.orgPlaceUser(uid, { department_id: "" }).then(reload).catch(err));
  const [zoom, setZoomS] = useState(() => saved("wz_org_zoom", 1));
  const [size, setSizeS] = useState(() => saved("wz_org_size", "normal"));
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const setZoom = (z) => { setZoomS(z); localStorage.setItem("wz_org_zoom", JSON.stringify(z)); };
  const setSize = (s) => { setSizeS(s); localStorage.setItem("wz_org_size", JSON.stringify(s)); };
  const dw = SIZES.find((s) => s[0] === size)?.[2] || 300;
  const [closed, setClosed] = useState(() => saved("wz_org_folded", []));
  const fold = { closed, toggle: (id) => setClosed((c) => { const n = c.includes(id) ? c.filter((x) => x !== id) : [...c, id]; localStorage.setItem("wz_org_folded", JSON.stringify(n)); return n; }) };
  const parents = ctx.departments.filter((d) => ctx.departments.some((x) => x.parent_id === d.id)).map((d) => d.id);
  const setAll = (n) => { setClosed(n); localStorage.setItem("wz_org_folded", JSON.stringify(n)); };
  const onExport = async () => {
    setBusy(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await Promise.all([...(ref.current?.querySelectorAll("img") || [])].map((im) => (im.complete ? null : new Promise((r) => { im.onload = im.onerror = r; setTimeout(r, 3000); }))));
    try { await exportPdf(ref.current, ctx.org_name); toast.success("تم تصدير الهيكل PDF"); }
    catch { toast.error("تعذّر التصدير"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="org-chart">
      <ChartToolbar zoom={zoom} setZoom={setZoom} size={size} setSize={setSize} onExport={onExport} busy={busy}
        fold={parents.length > 0 && <Button size="sm" variant="ghost" className="gap-1 text-xs" onClick={() => setAll(closed.length ? [] : parents)} data-testid="org-fold-all">
          {closed.length ? <><ChevronDown className="h-4 w-4" /> فتح كل الفروع</> : <><ChevronUp className="h-4 w-4" /> طي كل الفروع</>}</Button>} />
      <div className="overflow-auto pb-2">
      <div ref={ref} className="space-y-8 bg-white p-2" style={{ zoom: busy ? 1 : zoom, "--dw": `${dw}px` }} data-testid="org-canvas">
        {busy && (
          <div className="flex items-center justify-between border-b-2 border-slate-900 pb-3">
            <div className="flex items-center gap-3">
              {ctx.has_logo && <img src={api.publicOrgLogoUrl(ctx.org_id)} alt="" crossOrigin="anonymous" className="h-14 w-14 rounded-xl object-contain" />}
              <div><p className="text-xl font-bold text-slate-900">{ctx.org_name}</p><p className="text-sm text-slate-500">الهيكل التنظيمي</p></div>
            </div>
            <p className="text-sm text-slate-500">تاريخ التصدير: {new Date().toLocaleDateString("ar-SA-u-ca-gregory", { year: "numeric", month: "long", day: "numeric" })}</p>
          </div>
        )}
      <div className="flex flex-col items-center">
        <div className="w-full max-w-md rounded-2xl bg-slate-900 p-4 text-white shadow-xl" data-testid="org-root">
          <p className="flex items-center gap-2 text-lg font-bold"><Building2 className="h-5 w-5 text-blue-300" /> {ctx.org_name || "الشركة"}</p>
          <p className="mt-0.5 text-xs text-slate-400">الملاك والشركاء — كل الصلاحيات</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {owners.map((u) => (
              <button key={u.uid} type="button" onClick={() => onOpen(u)} data-testid={`org-owner-${u.uid}`} className="flex items-center gap-2 rounded-full bg-white/10 py-1 pe-3 ps-1 text-sm transition-colors hover:bg-white/20">
                <Avatar u={u} size="h-6 w-6" /> {u.name}{u.is_primary_owner && <Crown className="h-3.5 w-3.5 text-amber-300" />}
              </button>
            ))}
          </div>
        </div>
        <span className="h-6 w-px bg-slate-300" />
        <span className="hidden h-px w-[80%] bg-slate-300 sm:block" />
      </div>
      <div className="flex flex-wrap items-start justify-center gap-x-5 gap-y-10 pt-2">
        {ctx.departments.filter((d) => !ctx.departments.some((p) => p.id === d.parent_id)).map((d) => branch(d, ctx, onOpen, reload, 0, fold))}
        <AddDept reload={reload} used={ctx.departments.map((d) => d.color)} />
      </div>
      </div>
      </div>
      <div {...dropF} data-testid="org-unassigned" className={`rounded-2xl border border-slate-200 bg-slate-50 p-4 transition-colors ${overF ? "ring-2 ring-slate-400" : ""}`}>
        <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700"><UserX className="h-4 w-4" /> بدون قسم ({free.length}) <span className="text-xs font-normal text-slate-500">— اسحب أي مستخدم إلى قسمه</span></p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {free.map((u) => <UserChip key={u.uid} u={u} ctx={ctx} onOpen={onOpen} />)}
        </div>
      </div>
    </div>
  );
};
